import { z } from 'zod';

export const ENVIRONMENT_VALUES = ['development', 'staging', 'production'] as const;
export type Environment = (typeof ENVIRONMENT_VALUES)[number];

export const OUTBOUND_DELIVERY_MODES = ['stub', 'live'] as const;
export type OutboundDeliveryMode = (typeof OUTBOUND_DELIVERY_MODES)[number];

export const envSchema = z
  .object({
    ENVIRONMENT: z.enum(ENVIRONMENT_VALUES).default('development'),
    VERSION: z.string().max(64).optional(),
    GIT_SHA: z.string().max(64).optional(),

    JWT_SECRET: z.string().min(16, 'JWT_SECRET must be at least 16 chars'),
    // JSON-encoded `kid → secret` map for the JWT rotation hook (#16).
    // Tokens minted after rotation is enabled carry a `kid` header;
    // verifyAccessToken looks the secret up in this map. Tokens
    // without a `kid` are verified against JWT_SECRET. Empty string
    // (the default in environments that haven't rotated yet) disables
    // kid-based lookup and is a valid value.
    JWT_KEYS_BY_KID: z
      .string()
      .optional()
      .transform((s) => {
        if (!s) return undefined;
        try {
          return JSON.parse(s) as Record<string, string>;
        } catch {
          throw new Error('JWT_KEYS_BY_KID must be valid JSON');
        }
      })
      .pipe(z.record(z.string(), z.string().min(16)).optional()),
    REFRESH_TOKEN_PEPPER: z.string().min(16, 'REFRESH_TOKEN_PEPPER must be at least 16 chars'),
    OTP_HMAC_KEY: z.string().min(16, 'OTP_HMAC_KEY must be at least 16 chars'),

    // The `Domain=` attribute on auth cookies (#12). Empty string
    // (the default in dev where the host is `127.0.0.1`) means browsers
    // default the cookie to the exact response host. Set to
    // `okbumbum.com` in production so the cookies are sent on
    // `okbumbum.com` AND every subdomain (api., cdn., www.). Setting
    // it to a parent domain is intentional and is the only way the
    // api-subdomain's session can be carried on a hypothetical future
    // front-end subdomain. The access_token cookie's `Path=/` makes
    // this safe; the refresh cookie's `Path=/api/v1/auth` keeps its
    // blast radius small.
    COOKIE_DOMAIN: z
      .string()
      .optional()
      .transform((s) => (s && s.length > 0 ? s : undefined)),

    // Open item #15: refresh-token replay grace. If a presented
    // token was rotated less than this many seconds ago, the second
    // presentation is treated as a benign race (return invalid,
    // don't revoke the family) instead of a theft signal (revoke
    // the family). Default 0 keeps the current strict behavior;
    // staging + production can set 5–10 to absorb the common
    // "tab A refreshed, tab B is still using the old cookie" case.
    REFRESH_TOKEN_REPLAY_GRACE_SEC: z.coerce.number().int().min(0).max(300).default(0),

    CORS_ALLOWED_ORIGINS: z
      .string()
      .min(1)
      .transform((s) =>
        s
          .split(',')
          .map((o) => o.trim())
          .filter((o) => o.length > 0),
      )
      .pipe(z.array(z.string().url()).min(1, 'CORS_ALLOWED_ORIGINS must contain at least one origin')),

    // Outbound delivery channel. 'stub' (default) logs the message to the
    // Worker's structured logs + writes an audit_logs row, so the Queue
    // pipeline is end-to-end testable without a live Twilio / email-sending
    // account. 'live' calls the real provider. Staging starts on 'stub';
    // flip to 'live' once the provider secrets are configured.
    OUTBOUND_DELIVERY_MODE: z
      .enum(OUTBOUND_DELIVERY_MODES)
      .default('stub'),

    // Twilio (live mode only). Optional in stub mode so the Worker can boot
    // without them.
    TWILIO_ACCOUNT_SID: z.string().optional(),
    TWILIO_AUTH_TOKEN: z.string().optional(),
    TWILIO_FROM_NUMBER: z.string().regex(/^\+[1-9]\d{6,14}$/).optional(),

    // Email sending (live mode only). The actual send uses the
    // Workers `send_email` binding (no API token), but the from
    // address is configured via the plain env var `EMAIL_FROM`.
    // The address must use a domain that has been onboarded to
    // Cloudflare Email Sending (Dashboard → Email Service → Sending
    // domains) — unverified senders are rejected with
    // E_SENDER_NOT_VERIFIED at send time.
    //
    // Format: "Display Name <addr@domain>" or just "addr@domain".
    EMAIL_FROM: z.string().min(1).optional(),
    EMAIL_REPLY_TO: z.string().email().optional(),

    // Cloudflare Images — drives the avatar pipeline. The IMAGES binding is
    // available in the runtime; the two env vars below are what the binding-
    // free code paths (e.g. tests, or the route that mints direct-creator-
    // upload URLs via the REST API) need.
    //
    // CF_IMAGES_TOKEN is a scoped API token with the "Account → Cloudflare
    // Images: Edit" permission (configuration.md §Authentication in the
    // cloudflare-images skill). IMAGES_ACCOUNT_HASH is the value of the
    // `delivery_url` hash shown on the Images dashboard; it appears in
    // every public image URL.
    //
    // Both are required in any environment where the avatar endpoint is
    // reachable. The 'images_disabled' value for CF_IMAGES_TOKEN is treated
    // as "feature off" by routes/avatar.ts — the endpoint returns 503
    // rather than calling the API.
    CF_IMAGES_TOKEN: z.string().min(1).optional(),
    IMAGES_ACCOUNT_HASH: z.string().min(1).optional(),

    // Instagram Graph API (Business Login for Instagram). When
    // `INSTAGRAM_APP_ID` is set, the /api/v1/instagram/* routes are
    // active; otherwise they return 503 (the link button is hidden
    // in the UI). All three are required for the link flow to work;
    // INSTAGRAM_TOKEN_ENCRYPTION_KEY must be 32 bytes of hex (64
    // chars) and is used to AES-256-GCM the long-lived token at
    // rest. Rotate the key by re-encrypting the row — the route
    // handles a missing key by treating existing rows as
    // undecryptable and prompting re-link, not by throwing.
    INSTAGRAM_APP_ID: z.string().min(1).optional(),
    INSTAGRAM_APP_SECRET: z.string().min(1).optional(),
    INSTAGRAM_REDIRECT_URI: z.string().url().optional(),
    INSTAGRAM_TOKEN_ENCRYPTION_KEY: z
      .string()
      .regex(/^[0-9a-fA-F]{64}$/, 'INSTAGRAM_TOKEN_ENCRYPTION_KEY must be 64 hex chars (32 bytes).')
      .optional(),
  })
  .strict()
  .passthrough()  // Allow Cloudflare bindings (DB, RATE_LIMITER, OUTBOUND, etc.) to pass through validation; the env middleware doesn't care about their shape.
  .superRefine((env, ctx) => {
    if (env.OUTBOUND_DELIVERY_MODE !== 'live') return;
    // Twilio is required only if the deploy ever sends an SMS OTP.
    // Email is the only channel in production today; if phone-OTP
    // is later enabled, set the three TWILIO_* env vars.
    const required: Array<keyof typeof env> = [
      'EMAIL_FROM',
    ];
    for (const key of required) {
      if (!env[key]) {
        ctx.addIssue({
          code: 'custom',
          path: [key],
          message: `${key} is required when OUTBOUND_DELIVERY_MODE=live`,
        });
      }
    }
  });

export type Env = z.infer<typeof envSchema>;

export type EnvValidationResult =
  | { ok: true; env: Env }
  | { ok: false; missingKeys: string[] };

export function validateEnv(raw: unknown): EnvValidationResult {
  const result = envSchema.safeParse(raw);
  if (result.success) {
    return { ok: true, env: result.data };
  }
  const missingKeys: string[] = [];
  for (const issue of result.error.issues) {
    // The strict-mode "unrecognized keys" error emits an issue with
    // empty path[] and the offending keys in `keys`. Surface those
    // so the error message actually names the unknown bindings
    // (DB, RATE_LIMITER, OUTBOUND, etc.) — the test pool's env
    // includes these by design.
    if (issue.code === 'unrecognized_keys') {
      const keys = (issue as unknown as { keys?: string[] }).keys ?? [];
      for (const k of keys) {
        if (!missingKeys.includes(k)) missingKeys.push(k);
      }
      continue;
    }
    const key = issue.path[0];
    if (typeof key === 'string' && !missingKeys.includes(key)) missingKeys.push(key);
  }
  return { ok: false, missingKeys };
}
